import { PAYMENT_FREQUENCIES, PAYMENT_PLAN_TYPES } from "../constants";
import { POST_LOGIN_CAMPAIGN_INTENT_SOURCE } from "../constants/postLoginExperience";
import { isSettlementOfferExpired } from "./settlementOffer";

export const CAMPAIGN_HOME_CTA_VARIANTS = Object.freeze({
  suggestedPlan: "suggested-plan",
  settlementOfferA: "settlement-offer-a",
  settlementOfferB: "settlement-offer-b",
  customPlan: "custom-plan",
  reducedOffer: "reduced-offer",
});

const CAMPAIGN_HOME_CTA_VALUES = new Set(
  Object.values(CAMPAIGN_HOME_CTA_VARIANTS)
);

export const normalizeCampaignHomeCta = (value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return CAMPAIGN_HOME_CTA_VALUES.has(normalizedValue)
    ? normalizedValue
    : null;
};

export const resolveCampaignHomeCta = ({
  accountId,
  campaignHomeIntent,
  hasQueryCta = false,
  queryCta,
}) => {
  const intentDebtId = String(campaignHomeIntent?.debtId || "").trim();
  const matchesCurrentDebt =
    !intentDebtId || intentDebtId === String(accountId || "").trim();
  const persistedCta = matchesCurrentDebt
    ? normalizeCampaignHomeCta(campaignHomeIntent?.cta)
    : null;
  const isAuthoritativePostLoginIntent =
    campaignHomeIntent?.source === POST_LOGIN_CAMPAIGN_INTENT_SOURCE;

  if (persistedCta && isAuthoritativePostLoginIntent) {
    return persistedCta;
  }

  if (hasQueryCta) {
    return normalizeCampaignHomeCta(queryCta);
  }

  return persistedCta;
};

const roundCurrency = (value) => Math.round(Number(value) * 100) / 100;

const numberOrNull = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeTermsOffered = (termsOffered) => {
  const terms = Array.isArray(termsOffered) ? termsOffered : [termsOffered];

  return [...new Set(
    terms
      .map((term) => Number(term))
      .filter((term) => Number.isFinite(term) && term > 0)
  )].sort((a, b) => a - b);
};

export const normalizeCampaignHomeOffer = (response) => {
  const currentBalance = numberOrNull(response?.currentBalance);
  const offerAmount = numberOrNull(response?.offerAmount);
  const termsOffered = normalizeTermsOffered(response?.termsOffered);
  const expiryDate =
    response?.expiryDate ??
    response?.expiry_date ??
    response?.offerExpiryDate ??
    response?.offer_expiry_date ??
    null;

  const hasValidAmounts =
    currentBalance !== null &&
    currentBalance > 0 &&
    offerAmount !== null &&
    offerAmount > 0 &&
    offerAmount < currentBalance;
  const hasSettlementInFull = termsOffered.includes(1);
  const scheduledTerms = termsOffered.filter((term) => term > 1);
  const highestScheduledTerm = scheduledTerms.at(-1) ?? null;

  if (
    !hasValidAmounts ||
    !hasSettlementInFull ||
    !highestScheduledTerm ||
    isSettlementOfferExpired(expiryDate)
  ) {
    return null;
  }

  const savingsAmount = roundCurrency(currentBalance - offerAmount);
  const savingsPercent = Math.round((savingsAmount / currentBalance) * 100);

  return {
    currentBalance,
    offerAmount,
    savingsAmount,
    savingsPercent,
    termsOffered,
    settlementInFullTerm: 1,
    highestScheduledTerm,
    expiryDate,
  };
};

export const campaignSettlementOffersFromOffer = (campaignOffer) => ({
  currentBalance: campaignOffer.currentBalance,
  offerAmount: campaignOffer.offerAmount,
  plans: campaignOffer.termsOffered,
});

export const buildCampaignSettleInFullPaymentPlan = ({
  accountId,
  campaignOffer,
  origin,
}) => ({
  planType: PAYMENT_PLAN_TYPES.settleInFull,
  numPayments: 1,
  standardPaymentAmount: campaignOffer.offerAmount,
  lastPaymentAmount: campaignOffer.offerAmount,
  paymentFrequency: PAYMENT_FREQUENCIES.monthly,
  totalPaymentAmount: campaignOffer.offerAmount,
  referenceNumber: accountId,
  expiryDate: campaignOffer.expiryDate,
  origin,
});

export const promoConfigFromCampaignOffer = (campaignOffer) => ({
  endDate: campaignOffer.expiryDate,
  payoff: {
    originalAmount: campaignOffer.currentBalance,
    offerAmount: campaignOffer.offerAmount,
    savingsPercent: campaignOffer.savingsPercent,
  },
});
